import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin, accountAccessShowResetPassword, modalClose } from 'actions/UiActions'
import { UTMConfig } from './types'
import AuthSuccess from './Modes/AuthSuccess'
import ForgotPassword from './Modes/ForgetPassword'
import Join from './Modes/Join'
import LoginStart from './Modes/LoginStart'
import LoginPassword from './Modes/LoginPassword'
import PartnershipLinkAccount from './Modes/PartnershipLinkAccount'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ResetPassword from './Modes/ResetPassword'
import { matchPath, useLocation } from 'react-router'
import { RESET_PASSWORD_PATH } from 'constants/url'
import config from 'constants/config'
import BusinessTravellerLoginForm from 'businessTraveller/components/business-account/BusinessTravellerLoginForm'
import BusinessTravellerPasswordLoginForm from 'businessTraveller/components/business-account/BusinessTravellerPasswordLoginForm'
import ModalSheet from 'components/Luxkit/Modal/ModalSheet'
import ModalBackdrop from 'components/Luxkit/Modal/ModalBackdrop'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import { getModalHeaderTitle } from './common'
import styled from 'styled-components'
import ScrollLock from 'components/Common/ScrollLock'

const DYNAMIC_HEADER_LIMIT = 70
const MODES_WITH_NO_BACK_BUTTON: Set<App.UiAccountModalMode> = new Set([
  'login',
  'linkPartnership',
  'resetPassword',
  'authSuccess',
  'tripPlannerLogin',
])

const Container = styled.div`
  overflow: hidden;
`

interface Props {
  modal: App.UiModalState;
  utm: App.UTMState;
  accountAccess: App.UiAccountModalState;
  partnership?: App.Partnership;
  processing?: boolean;
  isNotLoggedIn: boolean;
}

function AccountAccessModal(props: Props) {
  const {
    modal,
    utm,
    accountAccess,
    partnership,
    processing,
    isNotLoggedIn,
  } = props
  const { mode, email } = accountAccess
  const dispatch = useAppDispatch()
  const location = useLocation()

  const utmConfig = useMemo<UTMConfig>(() => {
    const utmTitle = utm.tl && decodeURIComponent(utm.tl)
    const hasValidUTMTitle = utmTitle && utmTitle.length < DYNAMIC_HEADER_LIMIT

    const utmConfig = {
      hideCloseBtn: false,
      title: hasValidUTMTitle ? utmTitle : undefined,
    }

    if (utm.join === '1') {
      return {
        ...utmConfig,
        hideCloseBtn: false,
      }
    }

    if (utm.join === '2') {
      return {
        ...utmConfig,
        hideCloseBtn: true,
      }
    }

    return utmConfig
  }, [utm])
  const open = modal.open && modal.modalType === 'accountAccess'
  const params = matchPath<{resetPasswordToken?: string}>(location.pathname, { path: `/:regionCode/${RESET_PASSWORD_PATH}` })?.params

  const modalTitle = getModalHeaderTitle(mode)

  useEffect(() => {
    if (!isNotLoggedIn) {
      return
    }

    if (params?.resetPasswordToken) {
      dispatch(accountAccessShowResetPassword())
    }
  }, [
    dispatch,
    params?.resetPasswordToken,
    isNotLoggedIn,
  ])

  const onModalClose = useCallback(() => {
    dispatch(modalClose())
  }, [dispatch])

  const onBackButtonClick = useCallback(() => {
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  const hideBackButton = MODES_WITH_NO_BACK_BUTTON.has(mode)

  return (
    <>
      <ScrollLock isActive={open} />
      {open &&
        <ModalBackdrop mode="default">
          <ModalSheet
            mode="default"
            height="auto"
            desktopHeight="auto"
            size="small"
          >
            <ModalHeader
              title={modalTitle}
              onBackButtonClick={hideBackButton ? undefined : onBackButtonClick}
              onCloseButtonClick={utmConfig.hideCloseBtn ? undefined : onModalClose}
            />
            <ModalBody>
              <ModalContent>
                <Container>
                  {processing && <LoadingIndicator visible={processing} floating />}
                  {mode === 'tripPlannerLogin' && <LoginStart open={open} mode={mode}/>}
                  {mode === 'login' && config.businessTraveller.currentAccountMode !== 'business' && <LoginStart open={open} />}
                  {mode === 'login' && config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerLoginForm isOpen={open} />}
                  {mode === 'loginPassword' && config.businessTraveller.currentAccountMode !== 'business' && email && <LoginPassword open={open} email={email} />}
                  {mode === 'loginPassword' && config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerPasswordLoginForm requestedEmail={email} isOpen={open} />}
                  {mode === 'join' && email && <Join open={open} email={email}/>}
                  {mode === 'forgotPassword' && email && <ForgotPassword open={open} email={email}/>}
                  {mode === 'resetPassword' && <ResetPassword open={open} token={params?.resetPasswordToken} />}
                  {mode === 'authSuccess' && <AuthSuccess heading={modal.heading} message={modal.message} />}
                  {partnership && mode === 'linkPartnership' && <PartnershipLinkAccount />}
                </Container>
              </ModalContent>
            </ModalBody>
          </ModalSheet>
        </ModalBackdrop>
      }
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    modal: state.ui.modal,
    processing: state.auth.processing,
    accountAccess: state.ui.accountAccess,
    utm: state.utm,
    partnership: state.geo.partnership,
    regionCode: state.geo.currentRegionCode,
    isHeadlessMode: state.config.headlessMode,
    isStoreMode: state.config.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessModal)
